import { memo } from 'react';
import { DateTimePicker } from '@/components/DateTimePicker';
import { GroupedDropdownlist } from '@/components/GroupedDropdownlist';
import type { Officer, Person } from '@/components/screens/OfficersAssigned';

const TIME_FIELDS = [
  { label: 'Dispatched', field: 'dispatched' },
  { label: 'Enroute', field: 'enroute' },
  { label: 'On Scene', field: 'onScene' },
  { label: 'Cleared', field: 'cleared' },
] as const;

function formatDate(date: any): string {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatTime(date: any): string {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

interface OfficersMobileCardsProps {
  officers: Officer[];
  people: Person[];
  officerLabel: string;
  officerPluralLabel: string;
  isReadOnly: boolean;
  editingField: { uuid: string; field: string } | null;
  editValue: any;
  datePickerOpen: boolean;
  datePickerPopupClass: string;
  officerFilterPreference: boolean | null;
  hasReassignPermission: boolean;
  hasActiveCall: boolean;
  autoOpenDropdown: boolean;
  onStartEditMobile: (uuid: string, field: string, value: any) => void;
  onCancelEdit: () => void;
  onTogglePrimary: (uuid: string) => void;
  onDeleteConfirm: (uuid: string) => void;
  onReassignConfirm: (uuid: string) => void;
  onSetMileage: (uuid: string) => void;
  onOfficerSelect: (item: any) => void;
  onOfficerCancel: () => void;
  onDropdownTab: (e: { shiftKey: boolean }) => void;
  onDropdownClose: () => void;
  onOfficerFilterChange: (data: { isOfficerFilterOnlineOnly: boolean }) => void;
  onDateChange: (v: any) => void;
  onDatePickerOpen: () => void;
  onDatePickerClose: () => void;
  onEnteringTextMode: () => void;
  onSaveEdit: () => void;
  onClearDate: () => void;
  onTabKey: (event: any) => void;
  buildCallCardUrl: (callCardId: number | null) => string;
}

export const OfficersMobileCards = memo(function OfficersMobileCards({
  officers,
  people,
  officerLabel,
  officerPluralLabel,
  isReadOnly,
  editingField,
  editValue,
  datePickerOpen,
  datePickerPopupClass,
  officerFilterPreference,
  hasReassignPermission,
  hasActiveCall,
  autoOpenDropdown,
  onStartEditMobile,
  onCancelEdit,
  onTogglePrimary,
  onDeleteConfirm,
  onReassignConfirm,
  onSetMileage,
  onOfficerSelect,
  onOfficerCancel,
  onDropdownTab,
  onDropdownClose,
  onOfficerFilterChange,
  onDateChange,
  onDatePickerOpen,
  onDatePickerClose,
  onEnteringTextMode,
  onSaveEdit,
  onClearDate,
  onTabKey,
  buildCallCardUrl,
}: OfficersMobileCardsProps) {
  const isEditing = (rowUuid: string, field: string): boolean => {
    return editingField?.uuid === rowUuid && editingField?.field === field;
  };

  return (
    <div className="mobile-cards">
      {officers.length === 0 ? (
        <div className="empty-state">
          <p>No {officerPluralLabel.toLowerCase()} assigned</p>
        </div>
      ) : (
        officers.map((officer) => (
          <div
            key={
              'mobile-' +
              officer.uuid +
              '-' +
              (officer.isBusy || officer.isQueued ? 'busy' : 'available')
            }
            className={`card${officer.isBusy || officer.isQueued ? ' busy-card' : ''}`}
          >
            {/* Card Header */}
            <div className="card-header">
              <div className="card-header-content">
                {isEditing(officer.uuid, 'name') ? (
                  <div className="edit-container mobile-edit">
                    <GroupedDropdownlist
                      value={editValue}
                      items={people}
                      labelKey="name"
                      valueKey="id"
                      groupBy="company"
                      placeholder={`Select ${officerLabel.toLowerCase()}...`}
                      initialIsOfficerFilterOnlineOnly={officerFilterPreference ?? undefined}
                      initialOpen={autoOpenDropdown}
                      onItemChange={onOfficerSelect}
                      onCancel={onOfficerCancel}
                      onTab={onDropdownTab}
                      onClose={onDropdownClose}
                      onFilterChange={onOfficerFilterChange}
                    />
                    <button className="btn-cancel" onClick={onCancelEdit}>
                      <i className="fas fa-times" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div
                      className={`card-officer-name${!isReadOnly ? ' editable' : ''}`}
                      onClick={() =>
                        onStartEditMobile(officer.uuid, 'name', officer.name)
                      }
                    >
                      {officer.name}
                      {!isReadOnly && (
                        <i className="fa-solid fa-pen-to-square edit-icon" />
                      )}
                    </div>
                    {!officer.isBusy && !officer.isQueued ? (
                      <button
                        className={`primary-badge${officer.isPrimary ? ' active' : ''}`}
                        onClick={() => onTogglePrimary(officer.uuid)}
                        disabled={isReadOnly}
                      >
                        PRI
                      </button>
                    ) : (
                      <span
                        className="primary-not-available-mobile"
                        title={`Busy ${officerPluralLabel.toLowerCase()} cannot be primary`}
                      >
                        N/A
                      </span>
                    )}
                  </>
                )}
              </div>
              <button
                className="btn-delete"
                onClick={() => onDeleteConfirm(officer.uuid)}
                disabled={isReadOnly}
                title={isReadOnly ? 'Edit mode required' : `Remove ${officerLabel}`}
                style={{ color: 'white' }}
              >
                Delete
              </button>
            </div>

            {/* Busy banner */}
            {(officer.isBusy || officer.isQueued) && (
              <div className="card-busy-banner">
                <div className="busy-icon-mobile">
                  <i className="fas fa-exclamation-triangle" />
                </div>
                <div className="busy-content-mobile">
                  <div className="busy-status-mobile">
                    {officer.busyReason || 'BUSY'}
                  </div>
                  <div className="busy-event-mobile">
                    Currently on Call{' '}
                    <a
                      href={buildCallCardUrl(officer.callCardId)}
                      target="_blank"
                      rel="noopener"
                    >
                      #{officer.currentEventId}
                    </a>
                  </div>
                  {hasReassignPermission && !hasActiveCall && (
                    <div className="reassign-hint">
                      Send this call to enable reassign.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Time fields grid */}
            {!officer.isBusy && !officer.isQueued && (
              <div className="card-grid">
                {TIME_FIELDS.map((fieldInfo) => (
                  <div key={fieldInfo.field} className="card-field">
                    <div className="card-field-label">{fieldInfo.label}</div>

                    {isEditing(officer.uuid, fieldInfo.field) ? (
                      <div className="edit-datetime mx-datepicker-mobile-fix">
                        <div className="datetime-edit-controls-mobile">
                          <DateTimePicker
                            value={editValue}
                            type="datetime"
                            format="DD MMM YYYY HH:mm:ss"
                            valueType="date"
                            showSecond={true}
                            clearable={false}
                            appendToBody={true}
                            popupClass={datePickerPopupClass}
                            open={datePickerOpen}
                            onOpen={onDatePickerOpen}
                            onClose={onDatePickerClose}
                            onEnteringTextMode={onEnteringTextMode}
                            onConfirm={onSaveEdit}
                            onClear={onClearDate}
                            onChange={onDateChange}
                            onKeyDown={onTabKey}
                          />
                          <button
                            className="btn-clear-date-mobile"
                            onClick={onClearDate}
                            title="Clear date/time"
                          >
                            <i className="fas fa-times-circle" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`card-field-value${!isReadOnly ? ' editable' : ''}`}
                        onClick={() =>
                          onStartEditMobile(
                            officer.uuid,
                            fieldInfo.field,
                            (officer as any)[fieldInfo.field]
                          )
                        }
                      >
                        {(officer as any)[fieldInfo.field] ? (
                          <>
                            <div className="datetime-display">
                              {formatDate((officer as any)[fieldInfo.field])}
                            </div>
                            <div className="time-display">
                              {formatTime((officer as any)[fieldInfo.field])}
                            </div>
                          </>
                        ) : (
                          <div className="empty-cell">&mdash;</div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Card actions */}
            <div className="card-actions">
              {hasReassignPermission && (officer.isBusy || officer.isQueued) ? (
                <button
                  className={`btn-reassign-mobile${!hasActiveCall ? ' is-disabled' : ''}`}
                  disabled={!hasActiveCall}
                  onClick={(e) => {
                    e.preventDefault();
                    if (hasActiveCall) onReassignConfirm(officer.uuid);
                  }}
                  title={
                    hasActiveCall
                      ? 'Clear from other call and reassign here'
                      : 'Send this call to enable reassignment'
                  }
                >
                  <i
                    className={
                      hasActiveCall ? 'fa-solid fa-right-long' : 'fa-solid fa-lock'
                    }
                    aria-hidden="true"
                  />
                  <span>Reassign</span>
                </button>
              ) : (
                <button
                  className="btn-message"
                  onClick={() => onSetMileage(officer.uuid)}
                  disabled={isReadOnly}
                  title={isReadOnly ? 'Edit mode required' : 'Mileage'}
                >
                  <img src="/Content/Images/icons8-speed-100.png" /> Mileage
                </button>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
});
